<template>
  <div class="bc-block">
    <div class="bc-head">
      <span>BC 解析参数模板</span>
      <span class="bc-tip">把账号关联到「BC解析」类型的参数模板；一个模板可关联多个账号，一个账号只能关联一个模板（已被占用的账号不可选）</span>
    </div>

    <div class="bc-list">
      <div class="bc-row" v-for="row in rows" :key="row.tplId">
        <div class="bc-name" :title="row.tplName">{{ row.tplName }}</div>
        <div class="bc-sep">:</div>
        <div class="bc-accs">
          <span class="bc-chip" v-for="u in row.accounts" :key="u">
            {{ u }}
            <el-popconfirm
              width="300px"
              title="解除该账号的关联？"
              confirm-button-text="解除"
              @confirm="removeAccount(u)"
            >
              <template #reference><i class="bc-x">×</i></template>
              <div>将解除账号「{{ u }}」与模板的关联，解除后不可恢复。</div>
            </el-popconfirm>
          </span>
        </div>
        <div class="bc-act">
          <em class="bc-edit" @click="openEdit(row)"><el-icon><EditPen /></el-icon></em>
          <el-popconfirm
            width="320px"
            title="删除整行关联？"
            confirm-button-text="删除"
            @confirm="removeRow(row)"
          >
            <template #reference><em class="bc-del"><el-icon><Delete /></el-icon></em></template>
            <div>将解除「{{ row.tplName }}」下 {{ row.accounts.length }} 个账号的关联，删除后不可恢复。</div>
          </el-popconfirm>
        </div>
      </div>
      <div class="bc-empty" v-if="!rows.length">还没有配置：点下方「+ 新增」为账号关联参数模板</div>
    </div>

    <el-button class="btn-add" v-if="!editor.open" @click="openAdd">+ 新增</el-button>

    <!-- 就地关联编辑器：不弹二级弹窗 -->
    <div class="bc-editor" v-if="editor.open">
      <div class="be-row">
        <div class="fld-tpl">
          <el-form-item label="参数模板" :error="errors.tpl">
            <el-select v-model="editor.tplId" :disabled="editor.mode === 'edit'" placeholder="请选择" style="width:100%">
              <el-option v-for="t in tplList" :key="t.id" :value="t.id" :label="t.name" />
            </el-select>
          </el-form-item>
        </div>
        <div class="fld-acc">
          <el-form-item label="账号 可多选" :error="errors.acc">
            <el-select
              v-model="editor.selected"
              multiple
              filterable
              collapse-tags
              collapse-tags-tooltip
              :disabled="!editor.tplId"
              :placeholder="editor.tplId ? '请选择' : '请先选择左侧的参数模板'"
              style="width:100%"
            >
              <el-option
                v-for="a in accountList"
                :key="a.username"
                :value="a.username"
                :label="a.username"
                :disabled="occByOther(a.username)"
              >
                <span>{{ a.username }}</span>
                <span v-if="occByOther(a.username)" class="bc-occ" :title="`已被「${tplName(bcMap[a.username])}」占用`">已被占用</span>
              </el-option>
              <template #empty><span class="bc-opt-empty">没有匹配的账号</span></template>
            </el-select>
            <span class="bc-count" v-if="editor.selected.length">已选 {{ editor.selected.length }}</span>
          </el-form-item>
        </div>
      </div>
      <div class="be-tip">
        {{ editor.mode === 'edit'
          ? '编辑已有模板：模板不可更换，可增减该模板下的账号。'
          : '先选参数模板，再勾选账号（可多选）；已被其它模板占用的账号会置灰。' }}
      </div>
      <div class="be-foot">
        <el-button size="small" @click="closeEditor">取消</el-button>
        <el-button size="small" type="primary" @click="editorConfirm">确定</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { EditPen, Delete } from '@element-plus/icons-vue';
  import { ElMessage } from 'element-plus';
  import * as Request from '@/assets/request';

  const emits = defineEmits(['change']);

  const props = defineProps({
    data: {
      type: Object,
      default: null
    },
    appGroupId: {
      type: [Number, String],
      default: null
    },
    assocAppId: {
      type: [Number, String],
      default: null
    }
  });

  // 唯一真源：username(string) -> 参数模板 id(number)
  const bcMap = ref({});
  // [{ id, name }]
  const tplList = ref([]);
  // [{ id, username, useCount, status, useModeName }]
  const accountList = ref([]);
  const editor = ref({ open: false, mode: 'add', tplId: null, selected: [] });
  const errors = ref({ tpl: '', acc: '' });

  const emitChange = () => emits('change', { account_bc_params_map: { ...bcMap.value } });

  // 归一化：键 string、值 number，避免 '1776' !== 1776 的隐性 bug
  const initBcMap = () => {
    const src = props.data?.params?.account_bc_params_map || {};
    const m = {};
    Object.keys(src).forEach(u => { m[u] = Number(src[u]); });
    bcMap.value = m;
  };

  const getTplList = async () => {
    if(!props.appGroupId || !props.assocAppId) return;
    const result = await Request.post({
      url: '/22001/facade/commons/params/template/getTemplateByType',
      data: { appGroup: props.appGroupId, appId: props.assocAppId }
    });
    if(result?.code != 200) return;
    tplList.value = (result?.data || []).map(i => ({ id: Number(i.id), name: i.displayName }));
  };

  const getAccountList = async () => {
    if(!props.appGroupId) return;
    const result = await Request.post({
      url: '/22001/facade/commons/carrier/account/getByCarrier',
      data: props.appGroupId
    });
    if(result?.code != 200) return;
    accountList.value = result?.data || [];
  };

  const tplName = id => tplList.value.find(t => t.id === Number(id))?.name || `模板 ${id}`;
  const isOccupied = u => Object.prototype.hasOwnProperty.call(bcMap.value, u);
  // 账号置灰条件：已被占用且不属于当前正在编辑的参数模板
  const occByOther = u => isOccupied(u) && bcMap.value[u] !== editor.value.tplId;

  // 按 tplId 归组；组顺序 = tplList 顺序（未登记的垫底），组内账号名升序
  const rows = computed(() => {
    const order = tplList.value.map(t => t.id);
    const groups = {};
    Object.keys(bcMap.value).forEach(u => {
      const id = bcMap.value[u];
      (groups[id] = groups[id] || []).push(u);
    });
    return Object.keys(groups)
      .map(k => ({ tplId: Number(k), tplName: tplName(Number(k)), accounts: groups[k].slice().sort() }))
      .sort((a, b) => {
        const ia = order.indexOf(a.tplId), ib = order.indexOf(b.tplId);
        return (ia < 0 ? 1e4 : ia) - (ib < 0 ? 1e4 : ib);
      });
  });

  const openAdd = () => {
    errors.value = { tpl: '', acc: '' };
    editor.value = { open: true, mode: 'add', tplId: null, selected: [] };
  };
  const openEdit = row => {
    errors.value = { tpl: '', acc: '' };
    editor.value = { open: true, mode: 'edit', tplId: row.tplId, selected: row.accounts.slice() };
  };
  const closeEditor = () => {
    editor.value = { open: false, mode: 'add', tplId: null, selected: [] };
    errors.value = { tpl: '', acc: '' };
  };

  // 新增态切换参数模板时重置已勾选账号
  watch(() => editor.value.tplId, () => {
    if(editor.value.mode === 'add') editor.value.selected = [];
  });

  const removeAccount = u => {
    delete bcMap.value[u];
    emitChange();
  };
  const removeRow = row => {
    row.accounts.forEach(u => { delete bcMap.value[u]; });
    emitChange();
  };

  const editorConfirm = () => {
    errors.value = { tpl: '', acc: '' };
    if(!editor.value.tplId) { errors.value.tpl = '请先选择参数模板'; return; }
    if(!editor.value.selected.length) { errors.value.acc = '请至少选择一个账号'; return; }

    const conflict = editor.value.selected.find(u => isOccupied(u) && bcMap.value[u] !== editor.value.tplId);
    if(conflict) {
      ElMessage.error(`「${conflict}」已被「${tplName(bcMap.value[conflict])}」占用`);
      return;
    }

    const tplId = editor.value.tplId;
    // 编辑态差集语义：该模板原先绑定但本次未保留的账号，一并解除
    Object.keys(bcMap.value).forEach(u => {
      if(bcMap.value[u] === tplId && !editor.value.selected.includes(u)) delete bcMap.value[u];
    });
    editor.value.selected.forEach(u => { bcMap.value[u] = tplId; });

    emitChange();
    ElMessage.success(`已关联 ${editor.value.selected.length} 个账号到「${tplName(tplId)}」`);
    closeEditor();
  };

  watch(() => props.data, () => {
    initBcMap();
    closeEditor();
  }, { deep: true });

  onMounted(() => {
    initBcMap();
    getTplList();
    getAccountList();
  });
</script>

<style scoped lang="less">
  @import "./bcDataSync.less";
</style>
